import { MenuItemData, MenuItemLink } from '../menu-item-data'
import { ReactNode, createContext, useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

export type MenuContext = {
  selected: MenuItemData | undefined
  setSelected: (selected: MenuItemData | undefined) => void
  showFocus: boolean
  setShowFocus: (showFocus: boolean) => void
  visitLink: (menuItem: MenuItemLink) => void
}

export const menuContext = createContext<MenuContext | undefined>(undefined)

export const MenuProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const router = useRouter()
  const [selected, _setSelected] = useState<MenuItemData | undefined>()
  const [showFocus, setShowFocus] = useState(false)

  const setSelected = useCallback(
    (menuItem: MenuItemData | undefined) => {
      _setSelected(menuItem)
      if (menuItem) {
        menuItem.sendEvent?.()
      }
    },
    [_setSelected]
  )

  const visitLink = useCallback(
    (menuItem: MenuItemLink) => {
      if (menuItem.isExternal) {
        menuItem.sendEvent?.()
        window.open(menuItem.href, '_blank', 'noopener,noreferrer')
      } else if (menuItem.href) {
        menuItem.sendEvent?.()
        router.push(menuItem.href)
      }
    },
    [router]
  )

  const value = useMemo(
    () => ({
      selected,
      setSelected,
      showFocus,
      setShowFocus,
      visitLink
    }),
    [setSelected, visitLink, selected, showFocus]
  )

  return <menuContext.Provider value={value}>{children}</menuContext.Provider>
}
